/* =====================================================================
 * Badge — 공통 뱃지 컴포넌트 (테마 기반)
 * ===================================================================== */

import React from 'react';
import styled, { css } from 'styled-components';

type BadgeVariant = 'default' | 'info' | 'success' | 'warning' | 'error' | 'accent';
type BadgeSize = 'sm' | 'md';

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  /** pill 형태 (border-radius 최대) */
  pill?: boolean;
  /** 커스텀 색상 (variant 무시) */
  color?: string;
}

export const Badge: React.FC<BadgeProps> = ({
  variant = 'default',
  size = 'sm',
  pill = false,
  color,
  children,
  ...props
}) => (
  <StyledBadge $variant={variant} $size={size} $pill={pill} $color={color} {...props}>
    {children}
  </StyledBadge>
);

/* ---- variant 색상 매핑 ---- */

const variantStyles = {
  default: css`
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
    color: ${({ theme }) =>
      theme.mode === 'dark' ? '#a1a1aa' : '#71717a'};
  `,
  info: css`
    background: ${({ theme }) =>
      theme.mode === 'dark' ? '#3b82f633' : '#dbeafe'};
    color: ${({ theme }) =>
      theme.mode === 'dark' ? '#60a5fa' : '#1e40af'};
  `,
  success: css`
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(16,163,127,0.12)' : 'rgba(16,163,127,0.06)'};
    color: ${({ theme }) => theme.colors.success};
  `,
  warning: css`
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(251,191,36,0.15)' : 'rgba(245,158,11,0.10)'};
    color: ${({ theme }) =>
      theme.mode === 'dark' ? '#fbbf24' : '#d97706'};
  `,
  error: css`
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(239,68,68,0.15)' : 'rgba(220,38,38,0.08)'};
    color: ${({ theme }) =>
      theme.mode === 'dark' ? '#f87171' : '#dc2626'};
  `,
  accent: css`
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.textInverse};
  `,
};

const StyledBadge = styled.span<{
  $variant: BadgeVariant;
  $size: BadgeSize;
  $pill: boolean;
  $color?: string;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  line-height: 1;

  ${({ $size }) =>
    $size === 'sm'
      ? css`
          padding: 2px 8px;
          font-size: 0.6875rem;
          min-height: 20px;
        `
      : css`
          padding: 4px 10px;
          font-size: 0.75rem;
          min-height: 24px;
        `}

  border-radius: ${({ $pill }) => ($pill ? '999px' : '4px')};

  ${({ $color, $variant }) =>
    $color
      ? css`
          background: ${$color}15;
          color: ${$color};
        `
      : variantStyles[$variant]}
`;
